import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { MapPin, Plus, Pencil, Trash2, ShieldCheck, Sparkles, Zap, Wand2, Check, X as XIcon } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_LABEL = { draft: 'Draft', needs_review: 'Needs Review', verified: 'Verified' };
const STATUS_TONE = {
  draft: 'bg-slate-100 text-slate-600',
  needs_review: 'bg-amber-100 text-amber-700',
  verified: 'bg-emerald-100 text-emerald-700',
};
const SOURCE_LABEL = {
  crm_data: 'CRM Data', agent_note: 'Agent Note', approved_itinerary: 'Approved Itinerary',
  external_research: 'External Research', ai_assisted: 'AI-Assisted',
};

const EMPTY_FORM = {
  country: '', region: '', citiesText: '', recommendedNights: '', bestTimeToVisit: '',
  visaOverview: '', areasToStay: { honeymoon: '', family: '', budget: '' },
  topExperiencesText: '', commonMistakesText: '', travcationsRecommendations: '',
  reasonForAdding: '', status: 'draft', source: 'agent_note',
};

const EMPTY_QUICK_FORM = { country: '', reasonForAdding: '', travcationsRecommendations: '' };

const toLines = (arr) => (Array.isArray(arr) ? arr.join('\n') : '');
const fromLines = (text) => (text || '').split('\n').map((s) => s.trim()).filter(Boolean);

// Session 3 — AI-assisted suggestion review card. Renders under a field once
// a pending suggestion exists for it; Accept/Edit/Reject each resolve it.
function SuggestionCard({ suggestion, onResolve, resolving }) {
  const [editText, setEditText] = useState(
    suggestion.suggestedList ? suggestion.suggestedList.join('\n') : (suggestion.suggestedText || '')
  );
  const [isEditing, setIsEditing] = useState(false);

  return (
    <div className="mt-1.5 rounded-lg border border-fuchsia-200 bg-fuchsia-50/60 p-2.5" data-testid={`suggestion-card-${suggestion.suggestionId}`}>
      {suggestion.flaggedAsGeneralKnowledge && (
        <div className="text-[10px] font-semibold text-amber-700 bg-amber-100 rounded px-1.5 py-0.5 mb-1.5 inline-block" data-testid={`suggestion-general-knowledge-flag-${suggestion.suggestionId}`}>
          General knowledge — not Travcations-verified
        </div>
      )}
      {isEditing ? (
        <Textarea value={editText} onChange={(e) => setEditText(e.target.value)} rows={3} className="text-xs bg-white" data-testid={`suggestion-edit-textarea-${suggestion.suggestionId}`} />
      ) : (
        <div className="text-xs text-slate-700 whitespace-pre-line" data-testid={`suggestion-text-${suggestion.suggestionId}`}>
          {suggestion.suggestedList ? suggestion.suggestedList.map((l, i) => `• ${l}`).join('\n') : suggestion.suggestedText}
        </div>
      )}
      <div className="flex gap-2 mt-2">
        {isEditing ? (
          <>
            <Button size="sm" disabled={resolving} onClick={() => onResolve(suggestion, 'edit', editText)} className="h-7 text-xs bg-fuchsia-600 hover:bg-fuchsia-700" data-testid={`suggestion-save-edit-${suggestion.suggestionId}`}>
              <Check className="h-3 w-3 mr-1" /> Save
            </Button>
            <Button size="sm" variant="outline" onClick={() => setIsEditing(false)} className="h-7 text-xs" data-testid={`suggestion-cancel-edit-${suggestion.suggestionId}`}>Cancel</Button>
          </>
        ) : (
          <>
            <Button size="sm" disabled={resolving} onClick={() => onResolve(suggestion, 'accept')} className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700" data-testid={`suggestion-accept-${suggestion.suggestionId}`}>
              <Check className="h-3 w-3 mr-1" /> Accept
            </Button>
            <Button size="sm" variant="outline" onClick={() => setIsEditing(true)} className="h-7 text-xs" data-testid={`suggestion-edit-${suggestion.suggestionId}`}>
              <Pencil className="h-3 w-3 mr-1" /> Edit
            </Button>
            <Button size="sm" variant="outline" disabled={resolving} onClick={() => onResolve(suggestion, 'reject')} className="h-7 text-xs text-rose-600 hover:text-rose-700" data-testid={`suggestion-reject-${suggestion.suggestionId}`}>
              <XIcon className="h-3 w-3 mr-1" /> Reject
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

export default function Destinations() {
  const { user } = useAuth();
  const role = (user?.role || '').toLowerCase();
  const canEdit = role === 'admin' || role === 'accounts';
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showQuickDialog, setShowQuickDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [quickForm, setQuickForm] = useState(EMPTY_QUICK_FORM);
  const [saving, setSaving] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [suggestingField, setSuggestingField] = useState(null);
  const [resolvingId, setResolvingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/destination-knowledge`);
      setItems(res.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load destinations');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY_FORM); setEditing(null); setSuggestions([]); setShowDialog(true); };
  const openQuickAdd = () => { setQuickForm(EMPTY_QUICK_FORM); setShowQuickDialog(true); };
  const populateForm = (d) => {
    setForm({
      country: d.country || '', region: d.region || '', citiesText: toLines(d.cities),
      recommendedNights: d.recommendedNights || '', bestTimeToVisit: d.bestTimeToVisit || '',
      visaOverview: d.visaOverview || '',
      areasToStay: { honeymoon: d.areasToStay?.honeymoon || '', family: d.areasToStay?.family || '', budget: d.areasToStay?.budget || '' },
      topExperiencesText: toLines(d.topExperiences), commonMistakesText: toLines(d.commonMistakes),
      travcationsRecommendations: d.travcationsRecommendations || '', reasonForAdding: d.reasonForAdding || '',
      status: d.status || 'draft', source: d.source || 'agent_note',
    });
  };
  const loadSuggestions = async (destId) => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/destination-knowledge/${destId}/suggestions`);
      setSuggestions((res.data || []).filter((s) => s.resolution === 'pending'));
    } catch (err) {
      // non-fatal — the field-level Suggest buttons still work without this
    }
  };
  const openEdit = (d) => {
    populateForm(d);
    setEditing(d.destinationId);
    setSuggestions([]);
    loadSuggestions(d.destinationId);
    setShowDialog(true);
  };

  const requestSuggestion = async (field) => {
    if (!editing) return;
    setSuggestingField(field);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/destination-knowledge/${editing}/suggest`, { field });
      setSuggestions((prev) => [...prev.filter((s) => s.field !== field), res.data]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to get AI suggestion');
    } finally {
      setSuggestingField(null);
    }
  };

  const resolveSuggestion = async (suggestion, action, editedText) => {
    setResolvingId(suggestion.suggestionId);
    try {
      await axios.post(`${BACKEND_URL}/api/destination-knowledge/suggestions/${suggestion.suggestionId}/resolve`, {
        action, editedText,
      });
      setSuggestions((prev) => prev.filter((s) => s.suggestionId !== suggestion.suggestionId));
      if (action !== 'reject') {
        const fresh = await axios.get(`${BACKEND_URL}/api/destination-knowledge/${editing}`);
        populateForm(fresh.data);
        toast.success(action === 'accept' ? 'Suggestion accepted' : 'Suggestion saved with your edits');
      } else {
        toast.success('Suggestion rejected');
      }
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to resolve suggestion');
    } finally {
      setResolvingId(null);
    }
  };

  const save = async () => {
    if (!form.country.trim()) { toast.error('Country is required'); return; }
    setSaving(true);
    const payload = {
      country: form.country, region: form.region, cities: fromLines(form.citiesText),
      recommendedNights: form.recommendedNights, bestTimeToVisit: form.bestTimeToVisit,
      visaOverview: form.visaOverview, areasToStay: form.areasToStay,
      topExperiences: fromLines(form.topExperiencesText), commonMistakes: fromLines(form.commonMistakesText),
      travcationsRecommendations: form.travcationsRecommendations, reasonForAdding: form.reasonForAdding,
      status: form.status, source: form.source,
    };
    try {
      if (editing) {
        await axios.put(`${BACKEND_URL}/api/destination-knowledge/${editing}`, payload);
        toast.success('Destination updated');
      } else {
        await axios.post(`${BACKEND_URL}/api/destination-knowledge`, payload);
        toast.success('Destination created');
      }
      setShowDialog(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save destination');
    } finally {
      setSaving(false);
    }
  };

  const saveQuickAdd = async () => {
    if (!quickForm.country.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      await axios.post(`${BACKEND_URL}/api/destination-knowledge`, {
        country: quickForm.country,
        reasonForAdding: quickForm.reasonForAdding,
        travcationsRecommendations: quickForm.travcationsRecommendations,
        status: 'draft',
        source: 'agent_note',
      });
      toast.success('Destination quick-added — fill in the rest whenever you have it');
      setShowQuickDialog(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to quick-add destination');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (d) => {
    if (!window.confirm(`Delete "${d.country}"?`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/destination-knowledge/${d.destinationId}`);
      toast.success('Destination deleted');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to delete destination');
    }
  };

  const SuggestButton = ({ field }) => (
    <button
      type="button"
      onClick={() => requestSuggestion(field)}
      disabled={suggestingField === field}
      data-testid={`suggest-ai-${field}`}
      className="text-[11px] font-medium text-fuchsia-600 hover:text-fuchsia-700 flex items-center gap-1 disabled:opacity-50"
    >
      <Wand2 className="h-3 w-3" /> {suggestingField === field ? 'Suggesting…' : 'Suggest with AI'}
    </button>
  );

  const SuggestionFor = ({ field }) => {
    const s = suggestions.find((x) => x.field === field);
    if (!s) return null;
    return <SuggestionCard suggestion={s} onResolve={resolveSuggestion} resolving={resolvingId === s.suggestionId} />;
  };

  return (
    <div className="space-y-6 p-6 max-w-6xl mx-auto" data-testid="destinations-page">
      <header className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <MapPin className="h-6 w-6 text-teal-600" /> Destination Knowledge
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            {canEdit ? 'Create and maintain destination notes for the team.' : 'View-only — ask an Admin or Accounts user to edit.'}
          </p>
        </div>
        {canEdit && (
          <div className="flex gap-2">
            <Button onClick={openQuickAdd} variant="outline" className="gap-2" data-testid="quick-add-destination-btn">
              <Zap className="h-4 w-4" /> Quick Add
            </Button>
            <Button onClick={openNew} className="gap-2 bg-teal-600 hover:bg-teal-700" data-testid="add-destination-btn">
              <Plus className="h-4 w-4" /> Add Destination
            </Button>
          </div>
        )}
      </header>

      {loading ? (
        <div className="text-center py-12 text-slate-400 text-sm">Loading…</div>
      ) : items.length === 0 ? (
        <Card className="text-center py-12 text-slate-400 text-sm" data-testid="no-destinations">
          No destinations yet. {canEdit && 'Add one to start building the knowledge base.'}
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="destinations-grid">
          {items.map((d) => (
            <Card key={d.destinationId} className="p-4" data-testid={`destination-card-${d.destinationId}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-semibold text-slate-900">{d.country}</div>
                  {d.region && <div className="text-xs text-slate-400">{d.region}</div>}
                </div>
                {canEdit && (
                  <div className="flex gap-1">
                    <button onClick={() => openEdit(d)} data-testid={`edit-destination-${d.destinationId}`} className="p-1 text-slate-400 hover:text-teal-600"><Pencil className="h-3.5 w-3.5" /></button>
                    <button onClick={() => remove(d)} data-testid={`delete-destination-${d.destinationId}`} className="p-1 text-slate-400 hover:text-rose-600"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                )}
              </div>
              {(d.cities || []).length > 0 && (
                <div className="text-xs text-slate-500 mt-2">{(d.cities || []).join(', ')}</div>
              )}
              {d.reasonForAdding && (
                <div className="text-xs text-slate-500 mt-2 italic">"{d.reasonForAdding}"</div>
              )}
              <div className="flex items-center gap-2 mt-3 flex-wrap">
                {d.isLimitedKnowledge && (
                  <span className="text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full flex items-center gap-1 bg-fuchsia-50 text-fuchsia-600 border border-dashed border-fuchsia-300" data-testid={`destination-limited-badge-${d.destinationId}`}>
                    <Sparkles className="h-3 w-3" /> New · Limited Knowledge
                  </span>
                )}
                <span className={`text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full flex items-center gap-1 ${STATUS_TONE[d.status] || STATUS_TONE.draft}`} data-testid={`destination-status-${d.destinationId}`}>
                  {d.status === 'verified' && <ShieldCheck className="h-3 w-3" />}
                  {STATUS_LABEL[d.status] || 'Draft'}
                </span>
                <span className="text-[10px] text-slate-400">{SOURCE_LABEL[d.source] || d.source}</span>
              </div>
              <div className="text-xs text-slate-500 mt-2" data-testid={`destination-completeness-${d.destinationId}`}>
                {d.completeness?.label || `${d.completeness?.filled || 0} of ${d.completeness?.total || 12} fields filled`}
              </div>
              <div className="w-full h-1.5 bg-slate-100 rounded-full mt-1.5 overflow-hidden">
                <div className="h-full bg-teal-500" style={{ width: `${((d.completeness?.filled || 0) / (d.completeness?.total || 12)) * 100}%` }} />
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showQuickDialog} onOpenChange={setShowQuickDialog}>
        <DialogContent className="max-w-md" data-testid="quick-add-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Zap className="h-4 w-4 text-fuchsia-600" /> Quick Add Destination</DialogTitle>
            <DialogDescription>For a new/offbeat destination — just capture the basics now, fill in the rest later one field at a time.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Name *</Label>
              <Input value={quickForm.country} onChange={(e) => setQuickForm((f) => ({ ...f, country: e.target.value }))} className="mt-1" placeholder="e.g. Azores, Ladakh, Uzbekistan" data-testid="quick-add-name-input" />
            </div>
            <div>
              <Label className="text-xs">Reason for adding</Label>
              <Input value={quickForm.reasonForAdding} onChange={(e) => setQuickForm((f) => ({ ...f, reasonForAdding: e.target.value }))} className="mt-1" placeholder="e.g. Client asked about it, trending on Instagram" data-testid="quick-add-reason-input" />
            </div>
            <div>
              <Label className="text-xs">Freeform notes</Label>
              <Textarea value={quickForm.travcationsRecommendations} onChange={(e) => setQuickForm((f) => ({ ...f, travcationsRecommendations: e.target.value }))} className="mt-1" rows={3} placeholder="Anything you already know — will show up under Travcations recommendations later" data-testid="quick-add-notes-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowQuickDialog(false)}>Cancel</Button>
            <Button onClick={saveQuickAdd} disabled={saving} className="bg-fuchsia-600 hover:bg-fuchsia-700" data-testid="save-quick-add-btn">
              {saving ? 'Adding…' : 'Quick Add'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="destination-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Destination' : 'Add Destination'}</DialogTitle>
            <DialogDescription>Country/region-level travel notes for the team.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Country *</Label>
                <Input value={form.country} onChange={(e) => setForm((f) => ({ ...f, country: e.target.value }))} className="mt-1" data-testid="destination-country-input" />
              </div>
              <div>
                <Label className="text-xs">Region (optional)</Label>
                <Input value={form.region} onChange={(e) => setForm((f) => ({ ...f, region: e.target.value }))} className="mt-1" data-testid="destination-region-input" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Reason for adding (optional)</Label>
              <Input value={form.reasonForAdding} onChange={(e) => setForm((f) => ({ ...f, reasonForAdding: e.target.value }))} className="mt-1" data-testid="destination-reason-input" />
            </div>
            <div>
              <Label className="text-xs">City/Cities covered (one per line)</Label>
              <Textarea value={form.citiesText} onChange={(e) => setForm((f) => ({ ...f, citiesText: e.target.value }))} className="mt-1" rows={2} data-testid="destination-cities-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Recommended nights (typical range)</Label>
                <Input value={form.recommendedNights} onChange={(e) => setForm((f) => ({ ...f, recommendedNights: e.target.value }))} className="mt-1" placeholder="e.g. 5-7 nights" data-testid="destination-nights-input" />
                {editing && <SuggestButton field="recommendedNights" />}
                {editing && <SuggestionFor field="recommendedNights" />}
              </div>
              <div>
                <Label className="text-xs">Best time to visit</Label>
                <Input value={form.bestTimeToVisit} onChange={(e) => setForm((f) => ({ ...f, bestTimeToVisit: e.target.value }))} className="mt-1" data-testid="destination-best-time-input" />
                {editing && <SuggestButton field="bestTimeToVisit" />}
                {editing && <SuggestionFor field="bestTimeToVisit" />}
              </div>
            </div>
            <div>
              <Label className="text-xs">Visa overview (one-line summary)</Label>
              <Input value={form.visaOverview} onChange={(e) => setForm((f) => ({ ...f, visaOverview: e.target.value }))} className="mt-1" data-testid="destination-visa-input" />
              {editing && <SuggestButton field="visaOverview" />}
              {editing && <SuggestionFor field="visaOverview" />}
            </div>
            <div>
              <Label className="text-xs">Areas to stay (by traveller type)</Label>
              <div className="grid grid-cols-3 gap-2 mt-1">
                <div>
                  <Input value={form.areasToStay.honeymoon} onChange={(e) => setForm((f) => ({ ...f, areasToStay: { ...f.areasToStay, honeymoon: e.target.value } }))} placeholder="Honeymoon" data-testid="destination-areas-honeymoon-input" />
                  {editing && <SuggestButton field="areasToStay.honeymoon" />}
                  {editing && <SuggestionFor field="areasToStay.honeymoon" />}
                </div>
                <div>
                  <Input value={form.areasToStay.family} onChange={(e) => setForm((f) => ({ ...f, areasToStay: { ...f.areasToStay, family: e.target.value } }))} placeholder="Family" data-testid="destination-areas-family-input" />
                  {editing && <SuggestButton field="areasToStay.family" />}
                  {editing && <SuggestionFor field="areasToStay.family" />}
                </div>
                <div>
                  <Input value={form.areasToStay.budget} onChange={(e) => setForm((f) => ({ ...f, areasToStay: { ...f.areasToStay, budget: e.target.value } }))} placeholder="Budget" data-testid="destination-areas-budget-input" />
                  {editing && <SuggestButton field="areasToStay.budget" />}
                  {editing && <SuggestionFor field="areasToStay.budget" />}
                </div>
              </div>
            </div>
            <div>
              <Label className="text-xs">Top experiences / must-dos (one per line)</Label>
              <Textarea value={form.topExperiencesText} onChange={(e) => setForm((f) => ({ ...f, topExperiencesText: e.target.value }))} className="mt-1" rows={3} data-testid="destination-experiences-input" />
              {editing && <SuggestButton field="topExperiences" />}
              {editing && <SuggestionFor field="topExperiences" />}
            </div>
            <div>
              <Label className="text-xs">Common mistakes / things to avoid (one per line)</Label>
              <Textarea value={form.commonMistakesText} onChange={(e) => setForm((f) => ({ ...f, commonMistakesText: e.target.value }))} className="mt-1" rows={3} data-testid="destination-mistakes-input" />
              {editing && <SuggestButton field="commonMistakes" />}
              {editing && <SuggestionFor field="commonMistakes" />}
            </div>
            <div>
              <Label className="text-xs">Travcations recommendations (internal notes)</Label>
              <Textarea value={form.travcationsRecommendations} onChange={(e) => setForm((f) => ({ ...f, travcationsRecommendations: e.target.value }))} className="mt-1" rows={2} data-testid="destination-internal-notes-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
                  <SelectTrigger className="mt-1" data-testid="destination-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="needs_review">Needs Review</SelectItem>
                    <SelectItem value="verified">Verified</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Source</Label>
                <Select value={form.source} onValueChange={(v) => setForm((f) => ({ ...f, source: v }))}>
                  <SelectTrigger className="mt-1" data-testid="destination-source-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="crm_data">CRM Data</SelectItem>
                    <SelectItem value="agent_note">Agent Note</SelectItem>
                    <SelectItem value="approved_itinerary">Approved Itinerary</SelectItem>
                    <SelectItem value="external_research">External Research</SelectItem>
                    <SelectItem value="ai_assisted">AI-Assisted</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-teal-600 hover:bg-teal-700" data-testid="save-destination-btn">
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
